/**
 * The API client - a fetch wrapper. It adds the session cookie (`credentials`).
 * Unlike web_admin it does NOT add `X-Site-Id`: the account API is USER-scoped,
 * not site-scoped. The same session cookie is what gives SSO with web_admin - in
 * dev localhost shares the cookie across ports, and in production the API sets
 * `Domain=<base>` on it (account.husly.app -> husly.app), so "Open editor" does
 * not ask for a second sign-in.
 *
 * It also carries the STAFF PREVIEW token when one is in the address bar. See
 * `previewMode.ts`: while an operator is looking at this portal as the customer
 * would, every request has to say so, and this is the one place every request
 * passes through.
 */
import { PREVIEW_HEADER, previewToken } from "./previewMode";

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown) {
    super(typeof body === "object" && body && "error" in body ? String((body as { error: unknown }).error) : `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

async function req<T>(path: string, init: RequestInit = {}, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { ...(init.headers as Record<string, string>) };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  // The staff preview, if one is in force. Added here rather than at the call
  // sites because this function is the single door every `api.*` call goes
  // through - a per-call version would be one forgotten screen away from a
  // portal that renders half the customer's records and none of the rest.
  const preview = previewToken();
  if (preview) headers[PREVIEW_HEADER] = preview;

  const res = await fetch(`/api${path}`, {
    ...init,
    headers,
    credentials: "include",
    body: body !== undefined ? JSON.stringify(body) : init.body,
  });

  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, data);
  return data as T;
}

export const api = {
  // `opts.headers` is the exception (billing status and cancellation need an
  // `X-Site-Id`, which the account API otherwise never sends because it is
  // user-scoped).
  get: <T>(path: string, opts?: { headers?: Record<string, string> }) =>
    req<T>(path, { method: "GET", headers: opts?.headers }),
  post: <T>(path: string, body?: unknown, opts?: { headers?: Record<string, string> }) =>
    req<T>(path, { method: "POST", headers: opts?.headers }, body),
  patch: <T>(path: string, body?: unknown) => req<T>(path, { method: "PATCH" }, body),
  put: <T>(path: string, body?: unknown) => req<T>(path, { method: "PUT" }, body),
  del: <T>(path: string) => req<T>(path, { method: "DELETE" }),
  /** Multipart upload (field "file") - the profile photo and the like. The
   *  browser sets the Content-Type itself.
   *
   *  It does NOT carry the preview token, and that is deliberate rather than an
   *  omission: a preview is read-only and the API refuses every non-GET under
   *  `/api/portal/` while one is in force, so a token here could only make an
   *  upload look like it might work. */
  upload: async <T>(path: string, file: File): Promise<T> => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`/api${path}`, { method: "POST", credentials: "include", body: fd });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new ApiError(res.status, data);
    return data as T;
  },
};
